import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './RetailIntelligenceShoppingPage.css';

const ENV_API_BASE = process.env.REACT_APP_RETAIL_INTELLIGENCE_API_URL || '';
const LOCAL_API_BASE = 'http://localhost:8220';
const PROXY_API_BASE = '/retail-intelligence-api';
const DEFAULT_API_BASE = ENV_API_BASE || (isBrowserLocalhost() ? LOCAL_API_BASE : PROXY_API_BASE);
const API_KEY_HEADER = 'X-Retail-Intelligence-API-Key';
const SERVICE_MODE = 'AIDRQ_STANDALONE';
const SAMSAM_COOKBOOK_URL =
  'https://www.foodsafetykorea.go.kr/portal/cookrcp/cookRcpBook.do?menu_no=5108&menu_grp=MENU_NEW03';

const FALLBACK_SCOPE_USERS = [
  {
    user_no: '1',
    display_name: '사용자 1',
    patients: [
      { patient_id: '101', display_name: '나' },
      { patient_id: '102', display_name: '가족 구성원' },
    ],
    default_patient_ids: ['101', '102'],
  },
];

const SHOPPING_CONTEXTS = [
  { value: 'ANY', label: '두루 보기' },
  { value: 'BREAKFAST', label: '아침' },
  { value: 'SNACK', label: '간식' },
  { value: 'SIDE_DISH', label: '반찬' },
  { value: 'DINNER', label: '저녁' },
];

const RECOMMENDATION_LOADING_STAGES = [
  '가족 건강목표를 확인하고 있어요',
  '먹고 싶은 분위기를 검색 계획으로 바꾸고 있어요',
  '오늘 먹을 메뉴와 상품을 고르고 있어요',
  '추천 이유를 가족 맞춤 설명으로 정리하고 있어요',
];

function isBrowserLocalhost() {
  if (typeof window === 'undefined') {
    return false;
  }
  return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
}

function normalizeBaseUrl(value) {
  return String(value || '').trim().replace(/\/$/, '');
}

function withApiKeyHeader(options, apiKey) {
  const normalizedApiKey = String(apiKey || '').trim();
  if (!normalizedApiKey) {
    return options;
  }
  return {
    ...options,
    headers: {
      ...(options.headers || {}),
      [API_KEY_HEADER]: normalizedApiKey,
    },
  };
}

async function requestJson(apiBase, path, options = {}, apiKey = '') {
  const endpoint = `${normalizeBaseUrl(apiBase)}${path}`;
  let response;
  try {
    response = await fetch(endpoint, withApiKeyHeader(options, apiKey));
  } catch (err) {
    const error = new Error('추천 서버에 연결하지 못했습니다. 잠시 후 다시 시도해 주세요.');
    error.cause = err;
    throw error;
  }

  const raw = await response.text();
  let body = {};
  try {
    body = raw ? JSON.parse(raw) : {};
  } catch {
    body = { raw };
  }
  if (!response.ok) {
    const error = new Error(body?.detail?.message || body?.detail || raw || `HTTP ${response.status}`);
    error.status = response.status;
    error.body = body;
    throw error;
  }
  return body;
}

function fetchPatientScopes(apiBase, apiKey) {
  return requestJson(apiBase, '/v1/retail/patient-scopes', {}, apiKey);
}

function postRecommendationFull(apiBase, payload, apiKey) {
  return requestJson(
    apiBase,
    '/v1/retail/recommendations/full',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    apiKey,
  );
}

function postConversationalRecommendationFull(apiBase, baseRequest, userRequestText, apiKey) {
  return requestJson(
    apiBase,
    '/v1/retail/recommendations/conversational/full',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        base_request: baseRequest,
        user_request: {
          text: userRequestText,
        },
      }),
    },
    apiKey,
  );
}

function normalizeScopeUsers(users) {
  if (!Array.isArray(users)) {
    return [];
  }
  return users
    .map((user) => {
      const patients = Array.isArray(user.patients)
        ? user.patients
            .filter((patient) => patient?.patient_id)
            .map((patient) => ({
              patient_id: String(patient.patient_id),
              display_name: patient.display_name || `가족 구성원 ${patient.patient_id}`,
            }))
        : [];
      return {
        user_no: String(user.user_no || ''),
        display_name: user.display_name || `사용자 ${user.user_no}`,
        patients,
        default_patient_ids: Array.isArray(user.default_patient_ids)
          ? user.default_patient_ids.map(String)
          : patients.map((patient) => patient.patient_id),
      };
    })
    .filter((user) => user.user_no && user.patients.length > 0);
}

function selectedPatientIdsForUser(user) {
  return user?.default_patient_ids?.length ? user.default_patient_ids : user?.patients?.map((patient) => patient.patient_id) || [];
}

function firstSentence(text, fallback = '') {
  const normalized = String(text || '').replace(/\s+/g, ' ').trim();
  if (!normalized) {
    return fallback;
  }
  const match = normalized.match(/^(.+?[.!?。]|.+?다\.)\s/);
  return match ? match[1] : normalized;
}

function formatNutritionValue(nutritionBasis) {
  if (!nutritionBasis) {
    return '';
  }
  const label = nutritionBasis.nutrient_label || nutritionBasis.nutrient_key || '영양성분';
  const value = nutritionBasis.nutrient_value;
  if (value === null || value === undefined || value === '') {
    return '';
  }
  return `${label} ${value}${nutritionBasis.unit || ''}`;
}

function buildProductReason(product) {
  const productName = product.product_name || product.sku_name || '추천 상품';
  if (product.why_recommended) {
    return String(product.why_recommended).replace(/\s+/g, ' ').trim();
  }
  return firstSentence(product.reason, `${productName}은 선택한 가족 건강목표와 연결된 추천 후보입니다.`);
}

function buildProductEvidence(product) {
  const basis = product.nutrition_basis || {};
  const rows = [];
  const nutrition = formatNutritionValue(basis);
  if (nutrition) {
    rows.push(`핵심 영양근거: ${nutrition}${basis.serving_basis ? ` / ${basis.serving_basis}` : ''}`);
  }
  if (basis.health_goal?.label || basis.health_goal?.code) {
    rows.push(`연결 건강목표: ${basis.health_goal.label || basis.health_goal.code}`);
  }
  if (basis.mapping_level || basis.match_status) {
    rows.push(`영양 매핑: ${basis.mapping_level || '-'} / ${basis.match_status || '-'}`);
  }
  return rows.slice(0, 3);
}

function buildProductNutritionRows(product) {
  const basis = product.nutrition_basis || {};
  const rows = [];
  const nutrition = formatNutritionValue(basis);
  if (nutrition) {
    rows.push(['확인한 영양성분', nutrition]);
  }
  if (basis.serving_basis) {
    rows.push(['기준', basis.serving_basis]);
  }
  if (basis.health_goal?.label || basis.health_goal?.code) {
    rows.push(['연결된 건강목표', basis.health_goal.label || basis.health_goal.code]);
  }
  if (basis.nutrition_source) {
    rows.push(['영양정보 기준', basis.nutrition_source]);
  }
  rows.push(['참고', '대표상품 평균값 기준이며 실제 상품과 조리 방식에 따라 달라질 수 있습니다.']);
  return rows;
}

function ProductDetailSections({ product, nutritionRows, evidenceRows, keyPrefix }) {
  return (
    <>
      {nutritionRows.length ? (
        <details>
          <summary>영양 정보 보기</summary>
          <dl className="ri-shopping-product-nutrition">
            {nutritionRows.map(([label, value]) => (
              <React.Fragment key={`${keyPrefix}-${label}`}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </React.Fragment>
            ))}
          </dl>
        </details>
      ) : null}
      <details>
        <summary>상세 근거 보기</summary>
        <ul>
          {evidenceRows.map((item) => (
            <li key={`${keyPrefix}-${item}`}>{item}</li>
          ))}
          {product.reason ? <li>{product.reason}</li> : null}
        </ul>
      </details>
    </>
  );
}

function cleanMealUsageTip(value) {
  let text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) {
    return '';
  }
  text = text.replace(/^(?:(?:열량|탄수화물|단백질|지방|나트륨)\s*(?:\([^)]+\))?\s*)+/i, '').trim();
  text = text.replace(/^['"‘’“”]?\s*나트륨\s*[·ㆍ]\s*당류\s*줄이는\s*요리\s*tip\s*/i, '').trim();
  text = text.replace(/^요리\s*tip\s*/i, '').trim();
  text = text.replace(/^(?:\d+(?:\.\d+)?\s*){2,}/, '').trim();
  text = text.replace(/\s+\d+(?:\.\d+)?\s*$/, '').trim();
  return text;
}

function cleanMealSubtitle(value) {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) {
    return '';
  }
  if (/^[\d\s.,·ㆍ-]+$/.test(text)) {
    return '';
  }
  if (/^\d{2,3}[\s.,·ㆍ-]+/.test(text)) {
    return '';
  }
  if (/나트륨\s*[·ㆍ]\s*당류\s*줄인/.test(text)) {
    return '';
  }
  if (/^(?:혼밥|도시락|간식)\s*(?:메인\s*)?요리$/i.test(text)) {
    return '';
  }
  return text;
}

function isSamsamSource(card) {
  const sourceType = String(card?.source_type || '').toUpperCase();
  const sourceName = String(card?.source_name || '');
  return sourceType.includes('MFDS_SAMSAM') || sourceName.includes('삼삼한 밥상');
}

function formatSourceVolume(value) {
  const raw = String(value || '').trim();
  if (!raw) {
    return '';
  }
  if (raw.endsWith('권')) {
    return raw;
  }
  const match = raw.match(/\d+/);
  return match ? `${Number(match[0])}권` : raw;
}

function formatSourcePage(value) {
  const raw = String(value || '').trim();
  if (!raw) {
    return '';
  }
  if (raw.endsWith('페이지')) {
    return raw;
  }
  const normalized = raw.replace(/\s+/g, '').toUpperCase();
  if (/^\d+-\d+$/.test(normalized) || /^\d+-[A-Z]$/.test(normalized)) {
    return `${normalized}페이지`;
  }
  const match = normalized.match(/^\d+$/);
  return match ? `${Number(match[0])}페이지` : raw;
}

function formatMealSourceLabel(card) {
  const sourceName = isSamsamSource(card) ? '식약처 삼삼한 밥상' : card?.source_name || card?.source_type || '출처';
  return [sourceName, formatSourceVolume(card?.source_volume), formatSourcePage(card?.source_page)].filter(Boolean).join(' ');
}

function formatMealSourceUrl(card) {
  return card?.source_url || (isSamsamSource(card) ? SAMSAM_COOKBOOK_URL : '');
}

function mealSummary(card) {
  return card?.taste_appeal_summary || card?.summary || card?.source_summary || '';
}

function selectedPatientSummary(patients) {
  const names = (patients || []).map((patient) => patient.display_name).filter(Boolean);
  if (!names.length) {
    return '추천 대상 미선택';
  }
  if (names.length === 1) {
    return names[0];
  }
  return `${names[0]} 외 ${names.length - 1}명`;
}

function shoppingContextLabel(value) {
  return SHOPPING_CONTEXTS.find((context) => context.value === value)?.label || '두루 보기';
}

function narrativeAvailable(llmExplanation) {
  return Boolean(llmExplanation?.narrative?.summary || llmExplanation?.narrative?.headline || llmExplanation?.narrative?.why_now);
}

function fallbackNarrative(response) {
  const goal = response?.health_goal?.label || '가족 건강목표';
  const productNames = (response?.recommended_products || [])
    .slice(0, 2)
    .map((product) => product.product_name || product.sku_name)
    .filter(Boolean);
  if (productNames.length) {
    return `${goal}에 맞춰 ${productNames.join(', ')} 중심으로 장보기 후보를 골랐습니다. 추천 상품과 오늘 메뉴를 함께 확인해 보세요.`;
  }
  return '가족 건강목표에 맞춰 장보기 후보와 오늘 메뉴를 함께 확인해 보세요.';
}

function RetailIntelligenceShoppingPage() {
  const [apiBase] = useState(DEFAULT_API_BASE);
  const [apiKey] = useState('');
  const [scopeUsers, setScopeUsers] = useState(FALLBACK_SCOPE_USERS);
  const [scopeLoadError, setScopeLoadError] = useState(null);
  const [form, setForm] = useState({
    user_no: FALLBACK_SCOPE_USERS[0].user_no,
    patient_ids: selectedPatientIdsForUser(FALLBACK_SCOPE_USERS[0]),
    shopping_context: 'ANY',
  });
  const [naturalLanguageRequest, setNaturalLanguageRequest] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingStageIndex, setLoadingStageIndex] = useState(0);
  const [response, setResponse] = useState(null);
  const [llmExplanation, setLlmExplanation] = useState(null);
  const [llmExplanationError, setLlmExplanationError] = useState(null);
  const [error, setError] = useState(null);
  const [conditionsExpanded, setConditionsExpanded] = useState(true);
  const [selectedProductIndex, setSelectedProductIndex] = useState(0);
  const selectedUser = useMemo(
    () => scopeUsers.find((user) => user.user_no === form.user_no) || scopeUsers[0],
    [form.user_no, scopeUsers],
  );
  const selectedPatients = useMemo(
    () => (selectedUser?.patients || []).filter((patient) => form.patient_ids.includes(patient.patient_id)),
    [form.patient_ids, selectedUser],
  );
  const allPatientsSelected =
    selectedUser?.patients?.length > 0 && selectedUser.patients.every((patient) => form.patient_ids.includes(patient.patient_id));
  const conditionSummary = useMemo(
    () =>
      [
        selectedPatientSummary(selectedPatients),
        shoppingContextLabel(form.shopping_context),
        naturalLanguageRequest.trim() ? '대화형 요청 반영' : '건강 장보기 참고 추천',
      ].join(' · '),
    [form.shopping_context, naturalLanguageRequest, selectedPatients],
  );
  const showFullConditions = !response || conditionsExpanded;
  const currentLoadingStage = RECOMMENDATION_LOADING_STAGES[
    Math.min(loadingStageIndex, RECOMMENDATION_LOADING_STAGES.length - 1)
  ];

  const requestPayload = useMemo(
    () => ({
      service_mode: SERVICE_MODE,
      user_no: String(form.user_no).trim(),
      patient_ids: form.patient_ids,
      shopping_context: form.shopping_context,
      max_products_per_goal: 3,
    }),
    [form],
  );

  const topProducts = useMemo(() => (response?.recommended_products || []).slice(0, 3), [response]);
  const mealCards = useMemo(() => (response?.today_meal_cards || []).slice(0, 2), [response]);
  const safeSelectedProductIndex = topProducts.length ? Math.min(selectedProductIndex, topProducts.length - 1) : 0;
  const selectedProduct = topProducts[safeSelectedProductIndex] || null;

  useEffect(() => {
    setSelectedProductIndex(0);
  }, [response?.recommendation_set_id]);

  useEffect(() => {
    if (!loading) {
      return undefined;
    }
    const timerId = window.setInterval(() => {
      setLoadingStageIndex((prev) => Math.min(prev + 1, RECOMMENDATION_LOADING_STAGES.length - 1));
    }, 4500);
    return () => window.clearInterval(timerId);
  }, [loading]);

  const selectProductCard = useCallback(
    (event, index) => {
      setSelectedProductIndex(index);
      const inline = index === 0 ? 'start' : index >= topProducts.length - 1 ? 'end' : 'center';
      event.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline });
    },
    [topProducts.length],
  );

  const resetResult = useCallback(() => {
    setResponse(null);
    setLlmExplanation(null);
    setLlmExplanationError(null);
    setError(null);
    setConditionsExpanded(true);
  }, []);

  useEffect(() => {
    let ignore = false;
    const timerId = window.setTimeout(async () => {
      try {
        const result = await fetchPatientScopes(apiBase, apiKey);
        if (ignore) {
          return;
        }
        const users = normalizeScopeUsers(result.users);
        if (!users.length) {
          return;
        }
        setScopeUsers(users);
        setScopeLoadError(null);
        setForm((prev) => {
          const nextUser = users.find((user) => user.user_no === prev.user_no) || users[0];
          const validPatientIds = new Set(nextUser.patients.map((patient) => patient.patient_id));
          const preservedPatientIds = prev.patient_ids.filter((patientId) => validPatientIds.has(patientId));
          return {
            ...prev,
            user_no: nextUser.user_no,
            patient_ids: preservedPatientIds.length ? preservedPatientIds : selectedPatientIdsForUser(nextUser),
          };
        });
      } catch (err) {
        if (!ignore) {
          setScopeLoadError(err.message || '가족 목록을 불러오지 못했습니다.');
        }
      }
    }, 250);

    return () => {
      ignore = true;
      window.clearTimeout(timerId);
    };
  }, [apiBase, apiKey]);

  const togglePatient = useCallback(
    (patientId) => {
      setForm((prev) => {
        const nextSelected = new Set(prev.patient_ids);
        if (nextSelected.has(patientId)) {
          nextSelected.delete(patientId);
        } else {
          nextSelected.add(patientId);
        }
        const orderedPatientIds = (selectedUser?.patients || [])
          .map((patient) => patient.patient_id)
          .filter((id) => nextSelected.has(id));
        return { ...prev, patient_ids: orderedPatientIds };
      });
      resetResult();
    },
    [resetResult, selectedUser],
  );

  const selectAllPatients = useCallback(() => {
    setForm((prev) => ({ ...prev, patient_ids: selectedPatientIdsForUser(selectedUser) }));
    resetResult();
  }, [resetResult, selectedUser]);

  const updateShoppingContext = useCallback(
    (shoppingContext) => {
      setForm((prev) => ({ ...prev, shopping_context: shoppingContext }));
      resetResult();
    },
    [resetResult],
  );

  const updateNaturalLanguageRequest = useCallback(
    (value) => {
      setNaturalLanguageRequest(value);
      resetResult();
    },
    [resetResult],
  );

  const runRecommendation = useCallback(async () => {
    setLoading(true);
    setLoadingStageIndex(0);
    setError(null);
    setResponse(null);
    setLlmExplanation(null);
    setLlmExplanationError(null);
    try {
      const conversationalText = naturalLanguageRequest.trim();
      const result = conversationalText
        ? await postConversationalRecommendationFull(apiBase, requestPayload, conversationalText, apiKey)
        : await postRecommendationFull(apiBase, requestPayload, apiKey);
      setResponse(result?.recommendation || null);
      setLlmExplanation(result?.llm_explanation || null);
      setConditionsExpanded(false);
    } catch (err) {
      setError(err.message || '추천 요청에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [apiBase, apiKey, naturalLanguageRequest, requestPayload]);

  return (
    <div className="ri-shopping-page">
      <header className="ri-shopping-header">
        <div>
          <h1>AI닥터큐 건강 장보기</h1>
          <p className="ri-shopping-subtitle">가족 건강목표에 맞춰 오늘 살 상품과 메뉴를 골라드려요</p>
          <p className="ri-shopping-mode-note">현재는 AI닥터큐 단독 건강 장보기 참고 추천으로 실행합니다.</p>
        </div>
        <span className="ri-shopping-mode">건강 장보기 참고 추천</span>
      </header>

      {showFullConditions ? (
        <section className="ri-shopping-panel ri-shopping-conditions">
          <div className="ri-shopping-section-title">
            <h2>필요한 정보만 선택하세요</h2>
            <span>건강 장보기 참고 추천</span>
          </div>

          <div className="ri-shopping-condition-grid">
            <div className="ri-shopping-field ri-shopping-family-field">
              <div className="ri-shopping-field-head">
                <span>오늘은 누구의 건강식을 준비하세요?</span>
                <button type="button" onClick={selectAllPatients} disabled={allPatientsSelected}>
                  가족 전체
                </button>
              </div>
              <div className="ri-shopping-family-list">
                {(selectedUser?.patients || []).map((patient) => (
                  <label key={patient.patient_id} className="ri-shopping-family-option">
                    <input
                      type="checkbox"
                      checked={form.patient_ids.includes(patient.patient_id)}
                      onChange={() => togglePatient(patient.patient_id)}
                    />
                    <span>{patient.display_name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="ri-shopping-field ri-shopping-context-field">
              <span>오늘은 무슨 이유로 장을 보실 계획이세요?</span>
              <div className="ri-shopping-segmented">
                {SHOPPING_CONTEXTS.map((context) => (
                  <button
                    key={context.value}
                    type="button"
                    className={form.shopping_context === context.value ? 'is-selected' : ''}
                    onClick={() => updateShoppingContext(context.value)}
                  >
                    {context.label}
                  </button>
                ))}
              </div>
            </div>

            <label className="ri-shopping-field ri-shopping-natural-request-field">
              <span>먹고 싶은 음식이나 기분을 자유롭게 적어보세요</span>
              <textarea
                value={naturalLanguageRequest}
                onChange={(event) => updateNaturalLanguageRequest(event.target.value)}
                rows={3}
                placeholder="예: 나 오늘 울적한데 기분 전환할 수 있는 상큼한 음식 뭐 없을까?"
              />
              <small>입력하면 대화형 LLM planner가 검색 계획을 만든 뒤 상품과 메뉴 추천에 반영합니다.</small>
            </label>
          </div>

          {scopeLoadError ? <p className="ri-shopping-note is-warning">{scopeLoadError}</p> : null}
          <p className="ri-shopping-note">
            GS 고객회원번호, 점포유형, 점포코드는 GS 연계 구매 가능 SKU 추천 모드에서만 필수로 확인합니다.
          </p>
        </section>
      ) : (
        <section className="ri-shopping-panel ri-shopping-condition-summary" aria-label="선택한 추천 조건">
          <div>
            <strong>선택한 조건</strong>
            <p>{conditionSummary}</p>
          </div>
          <button type="button" onClick={() => setConditionsExpanded(true)}>
            조건 수정
          </button>
        </section>
      )}

      {showFullConditions ? (
        <div className="ri-shopping-action-bar">
          <button type="button" onClick={runRecommendation} disabled={loading || form.patient_ids.length === 0}>
            {loading ? '추천을 고르고 있어요' : response ? '다시 추천 받기' : '추천 받기'}
          </button>
        </div>
      ) : null}

      {error ? (
        <section className="ri-shopping-panel ri-shopping-error">
          <h2>추천을 불러오지 못했습니다</h2>
          <p>{error}</p>
        </section>
      ) : null}

      {loading ? (
        <section className="ri-shopping-panel ri-shopping-loading" aria-live="polite" aria-busy="true">
          <div className="ri-shopping-loading-head">
            <span>추천 준비 중</span>
            <strong>{currentLoadingStage}</strong>
          </div>
          <ol className="ri-shopping-loading-steps">
            {RECOMMENDATION_LOADING_STAGES.map((stage, index) => (
              <li
                key={stage}
                className={
                  index < loadingStageIndex
                    ? 'is-complete'
                    : index === loadingStageIndex
                    ? 'is-active'
                    : ''
                }
              >
                <span>{index + 1}</span>
                <p>{stage}</p>
              </li>
            ))}
          </ol>
          <p className="ri-shopping-muted">
            AI 설명까지 함께 준비한 뒤 한 번에 보여드릴게요. 잠시만 기다려 주세요.
          </p>
        </section>
      ) : response ? (
        <main className="ri-shopping-results">
          <section className="ri-shopping-panel ri-shopping-products-panel">
            <div className="ri-shopping-section-title">
              <h2>추천 상품</h2>
              <span>우선 확인할 3개</span>
            </div>
            <div className="ri-shopping-product-list ri-shopping-product-list-desktop">
              {topProducts.map((product) => {
                const nutritionRows = buildProductNutritionRows(product);
                const evidenceRows = buildProductEvidence(product);
                return (
                  <article key={`${product.rank}-${product.gs_product_id}`} className="ri-shopping-product-card">
                    <div className="ri-shopping-rank">#{product.rank}</div>
                    <div className="ri-shopping-product-body">
                      <h3>{product.product_name || product.sku_name || '추천 상품'}</h3>
                      {product.category ? <p className="ri-shopping-muted">{product.category}</p> : null}
                      <p className="ri-shopping-strong-text">{buildProductReason(product)}</p>
                      <ProductDetailSections
                        product={product}
                        nutritionRows={nutritionRows}
                        evidenceRows={evidenceRows}
                        keyPrefix={`desktop-${product.rank}`}
                      />
                    </div>
                  </article>
                );
              })}
            </div>
            <div className="ri-shopping-product-mobile-picker">
              <div className="ri-shopping-product-carousel" aria-label="추천 상품 후보">
                {topProducts.map((product, index) => {
                  const isSelected = index === safeSelectedProductIndex;
                  const nutrition = formatNutritionValue(product.nutrition_basis);
                  return (
                    <button
                      key={`${product.rank}-${product.gs_product_id}-choice`}
                      type="button"
                      className={`ri-shopping-product-choice ${isSelected ? 'is-selected' : ''}`}
                      aria-pressed={isSelected}
                      onClick={(event) => selectProductCard(event, index)}
                    >
                      <span className="ri-shopping-choice-topline">
                        <span className="ri-shopping-choice-rank">#{product.rank}</span>
                        {isSelected ? <span className="ri-shopping-choice-selected">선택됨</span> : null}
                      </span>
                      <strong>{product.product_name || product.sku_name || '추천 상품'}</strong>
                      {product.category ? <span className="ri-shopping-choice-category">{product.category}</span> : null}
                      <span className="ri-shopping-choice-reason">{buildProductReason(product)}</span>
                      {nutrition ? <span className="ri-shopping-choice-metric">{nutrition}</span> : null}
                    </button>
                  );
                })}
              </div>
              {selectedProduct ? (
                <article className="ri-shopping-product-selected-detail">
                  <div className="ri-shopping-selected-product-head">
                    <span>선택한 추천 상품</span>
                    <strong>#{selectedProduct.rank}</strong>
                  </div>
                  <h3>{selectedProduct.product_name || selectedProduct.sku_name || '추천 상품'}</h3>
                  {selectedProduct.category ? <p className="ri-shopping-muted">{selectedProduct.category}</p> : null}
                  <p className="ri-shopping-strong-text">{buildProductReason(selectedProduct)}</p>
                  <ProductDetailSections
                    product={selectedProduct}
                    nutritionRows={buildProductNutritionRows(selectedProduct)}
                    evidenceRows={buildProductEvidence(selectedProduct)}
                    keyPrefix={`mobile-${selectedProduct.rank}`}
                  />
                </article>
              ) : null}
            </div>
          </section>

          <section className="ri-shopping-panel ri-shopping-meals-panel">
            <div className="ri-shopping-section-title">
              <h2>오늘 이렇게 먹기</h2>
              <span>추천 메뉴 {mealCards.length}개</span>
            </div>
            {mealCards.length ? (
              <div className="ri-shopping-meal-list">
                {mealCards.map((card) => (
                  <article key={card.menu_id} className="ri-shopping-meal-card">
                    <div>
                      <h3>{card.title}</h3>
                      {cleanMealSubtitle(card.subtitle) ? <p className="ri-shopping-muted">{cleanMealSubtitle(card.subtitle)}</p> : null}
                      {mealSummary(card) ? <p className="ri-shopping-strong-text">{mealSummary(card)}</p> : null}
                      {card.source_summary && card.source_summary !== mealSummary(card) ? <p>{card.source_summary}</p> : null}
                      {cleanMealUsageTip(card.usage_tip) ? (
                        <div className="ri-shopping-tip">
                          <strong>요리 Tip</strong>
                          <p>{cleanMealUsageTip(card.usage_tip)}</p>
                        </div>
                      ) : null}
                    </div>

                    <div className="ri-shopping-meal-grid ri-shopping-meal-grid-desktop">
                      <div>
                        <strong>연결 상품</strong>
                        <p>{(card.matched_product_names || []).join(', ') || '-'}</p>
                      </div>
                      <div>
                        <strong>핵심 재료군</strong>
                        <p>{(card.required_product_groups || []).join(', ') || '-'}</p>
                      </div>
                      <div>
                        <strong>영양 요약</strong>
                        <p>{card.nutrition_summary || '-'}</p>
                      </div>
                      <div>
                        <strong>출처</strong>
                        {formatMealSourceUrl(card) ? (
                          <p>
                            <a href={formatMealSourceUrl(card)} target="_blank" rel="noreferrer">
                              {formatMealSourceLabel(card)}
                            </a>
                          </p>
                        ) : (
                          <p>{formatMealSourceLabel(card)}</p>
                        )}
                      </div>
                    </div>

                    <details className="ri-shopping-meal-info-details">
                      <summary>요리 정보 보기</summary>
                      <div className="ri-shopping-meal-grid">
                        <div>
                          <strong>연결 상품</strong>
                          <p>{(card.matched_product_names || []).join(', ') || '-'}</p>
                        </div>
                        <div>
                          <strong>핵심 재료군</strong>
                          <p>{(card.required_product_groups || []).join(', ') || '-'}</p>
                        </div>
                        <div>
                          <strong>영양 요약</strong>
                          <p>{card.nutrition_summary || '-'}</p>
                        </div>
                        <div>
                          <strong>출처</strong>
                          {formatMealSourceUrl(card) ? (
                            <p>
                              <a href={formatMealSourceUrl(card)} target="_blank" rel="noreferrer">
                                {formatMealSourceLabel(card)}
                              </a>
                            </p>
                          ) : (
                            <p>{formatMealSourceLabel(card)}</p>
                          )}
                        </div>
                      </div>
                    </details>

                    {card.ingredients_text ? (
                      <details className="ri-shopping-recipe-details">
                        <summary>재료 보기</summary>
                        <p>{card.ingredients_text}</p>
                      </details>
                    ) : null}

                    {(card.recipe_steps || []).length ? (
                      <details className="ri-shopping-recipe-details">
                        <summary>만드는 법 보기</summary>
                        <ol>
                          {card.recipe_steps.map((step, index) => (
                            <li key={`${card.menu_id}-step-${index}`}>{String(step).replace(/^\d+[.)]?\s*/, '')}</li>
                          ))}
                        </ol>
                      </details>
                    ) : null}

                  </article>
                ))}
              </div>
            ) : (
              <p className="ri-shopping-muted">현재 조건에 맞는 메뉴 후보가 없습니다. 추천 상품을 기준으로 장보기를 먼저 확인해 주세요.</p>
            )}
          </section>

          <section className="ri-shopping-panel ri-shopping-ai-summary">
            <div className="ri-shopping-section-title">
              <h2>AI 가족 맞춤 설명</h2>
              <span>{response.health_goal?.label || response.health_goal?.code || '가족 건강목표 기준'}</span>
            </div>
            {narrativeAvailable(llmExplanation) ? (
              <div className="ri-shopping-narrative">
                {llmExplanation.narrative?.headline ? <h3>{llmExplanation.narrative.headline}</h3> : null}
                {llmExplanation.narrative?.summary ? <p>{llmExplanation.narrative.summary}</p> : null}
                {llmExplanation.narrative?.why_now ? <p>{llmExplanation.narrative.why_now}</p> : null}
                {llmExplanation.narrative?.safety_note ? (
                  <p className="ri-shopping-safety-note">{llmExplanation.narrative.safety_note}</p>
                ) : null}
              </div>
            ) : (
              <div className="ri-shopping-narrative">
                <p>{fallbackNarrative(response)}</p>
                {llmExplanationError ? <p className="ri-shopping-muted">{llmExplanationError}</p> : null}
              </div>
            )}
          </section>
        </main>
      ) : (
        <section className="ri-shopping-empty">
          <strong>조건을 고른 뒤 추천 받기를 눌러 주세요.</strong>
          <span>추천 상품, 오늘 메뉴, 가족 맞춤 설명을 한 화면에서 확인할 수 있습니다.</span>
        </section>
      )}
    </div>
  );
}

export default RetailIntelligenceShoppingPage;
